import React, {useMemo, useState, useCallback} from "react";
import dayjs from "dayjs";
import {useQuery} from "@apollo/client";

import {EmptyList} from "../../components/empty-list";
import {BaseModalArgs, ModalHook, useModal, useNewModal} from "../../modals";
import {Button, InputRow, Text} from "../../components/input";
import {Span} from "../../components/text";
import {Visitor} from "./find-out-who-card";
import {downloadCsv} from "../../utils/csv";
import {GET_SALESFORCE_INTEGRATION, SYNC_SALESFORCE_LEADS, useMyUser} from "../../data";
import {useMutationToast, useToast} from "../../toast";
import {Icon} from "../../components/images";
import {ErrorsLogModal} from "../settings/integrations/salesforce";

import styles from "./find-out-who-modal.module.scss";

interface FindOutWhoArgs<T> extends BaseModalArgs<T> {
	visitors: Visitor[];
	title?: string;
}

const getFieldValue = (visitor: Visitor, field: string) => {
	let value = visitor?.[field];

	if (field === "location") {
		const locationField: string[] = [];
		if (visitor.city) {
			locationField.push(visitor.city);
		}
		if (visitor.state) {
			locationField.push(visitor.state);
		}
		value = locationField.length && locationField.join(", ");
	}

	if (field === "name") {
		value = visitor.as_name || visitor.name;
	}
	if (field === "employees") {
		value = visitor.employees;
	}
	if (field === "site_url") {
		value = visitor.site_url || visitor.url;
	}
	if (field === "contact") {
		value = visitor.phone_number || visitor.phoneNumber || visitor.email;
	}
	if (field === "date_visited") {
		value =
			(visitor.date_visited || visitor.dateVisited) &&
			dayjs(visitor.date_visited || visitor.dateVisited).formatAs("shortDate");
	}

	return value || "-";
};

const SyncSalesforceLeads = () => {
	const {data, loading} = useQuery(GET_SALESFORCE_INTEGRATION);
	const [syncSalesforceLeads, {loading: syncing}] = useMutationToast(SYNC_SALESFORCE_LEADS);
	const {open: errorsLogOpen, modal: errorsLogModal} = useNewModal({zIndex: 1000});
	const toast = useToast();

	const onSync = useCallback(async () => {
		const response = await syncSalesforceLeads();

		if (!response?.errors) {
			toast({
				text: "Salesforce synced successfully!",
				color: "green",
			});
		}
	}, [toast, syncSalesforceLeads]);

	if (loading || !data?.salesforceIntegration?.connected) return null;

	return (
		<>
			<Button
				onClick={onSync}
				loading={syncing}
				value="Sync with Salesforce"
				disabled={syncing}
				icon="refresh"
				invert
			/>
			<div className={styles.salesforceSyncStatus}>
				{data?.salesforceIntegration?.lastSyncedAt && (
					<Span color="grey">Latest sync: {dayjs(data.salesforceIntegration.lastSyncedAt).formatAs()}</Span>
				)}
				{data?.salesforceIntegration?.lastSyncStatus && (
					<Span color="grey">
						Sync status:{" "}
						{data?.salesforceIntegration?.lastSyncStatus === "success" ? (
							<Span color="blue">Successful</Span>
						) : (
							<Span className={styles.openErrors} color="pink" onClick={errorsLogOpen}>
								Sync errors
								<Icon color="pink" icon="goto-link" />
							</Span>
						)}
					</Span>
				)}
			</div>
			<ErrorsLogModal modal={errorsLogModal} />
		</>
	);
};

export function useFindOutWhoModal<T>({visitors, title}: FindOutWhoArgs<T>): ModalHook<T> {
	const me = useMyUser();
	const [search, setSearch] = useState("");
	const csvData = useMemo(() => {
		const headers = ["Company", "Website", "Location", "Employees", "Contact", "Date Visited", "Clicks"];
		const searchTrimmed = search.trim();
		const searchLC = search.toLowerCase();

		const items = visitors
			.filter(
				visitors =>
					!searchTrimmed ||
					visitors?.name?.toLowerCase()?.includes(searchLC) ||
					visitors?.as_name?.toLowerCase()?.includes(searchLC)
			)
			.map(visitor => [
				getFieldValue(visitor, "name"),
				getFieldValue(visitor, "site_url"),
				getFieldValue(visitor, "location"),
				getFieldValue(visitor, "employees"),
				getFieldValue(visitor, "contact"),
				getFieldValue(visitor, "date_visited"),
				getFieldValue(visitor, "clicks"),
			]);

		return [headers, ...items];
	}, [visitors, search]);

	const isEmpty = csvData.length <= 1;

	const handleExport = useCallback(() => {
		downloadCsv(csvData.slice(1), csvData[0], "find-out-who.csv");
	}, [csvData]);

	return useModal(
		() => ({
			size: "large",
			header: <h3>{title || "Find Out Who (Last 30 days)"}</h3>,
			body: (
				<>
					<InputRow>
						<Text icon="search" value={search} onChange={setSearch} placeholder="Search" />
						<Button onClick={handleExport} value="Export CSV" />
						{me.role === "admin" && me?.org?.features?.salesforceLeadsSync && <SyncSalesforceLeads />}
					</InputRow>

					<div className={styles.items}>
						{csvData.map((row, rowIndex) => (
							<div className={styles.row} key={rowIndex}>
								{row.map((rowItem, rowItemIndex) => (
									<Span key={rowItemIndex} bold={rowIndex === 0} className={styles.rowItem}>
										{rowItem}
									</Span>
								))}
							</div>
						))}
						{isEmpty && <EmptyList text={search ? "No matches found" : "No items"} />}
					</div>
				</>
			),
		}),
		[csvData, search, isEmpty, handleExport, title, me?.role, me?.org?.features?.salesforceLeadsSync]
	);
}
